import { useEffect, useRef, useState } from "react";
import { getToken } from "../utils/helpers";
import { createLocalRevisionWatcher } from "./cicdLocalRevision";

const streams = new Map();
const FALLBACK_MS = 60000;

// A page can observe a batch and its Edge follow-ups through one scoped connection.
export function subscribeCicdEvents(spaceId, listener, connection = () => {}, { localRevision = false, reconcileOnConnect = false } = {}) {
  let token;
  try { token = getToken(); } catch { return () => {}; }
  if (!spaceId || !token) return () => {};
  const key = JSON.stringify([spaceId, token]);
  let stream = streams.get(key);
  if (!stream) {
    stream = { listeners: new Set(), state: "connecting", stopped: false, revision: null, backoff: 1000 };
    streams.set(key, stream);
    const state = value => { stream.state = value; stream.listeners.forEach(row => row.connection(value)); };
    stream.pendingRows = new Set();
    const notify = (kind = "all") => {
      stream.listeners.forEach(row => { if (kind === "all" || (kind === "handshake" ? row.reconcileOnConnect
        : (kind === "local") === row.localRevision)) stream.pendingRows.add(row); });
      clearTimeout(stream.updateTimer);
      stream.updateTimer = setTimeout(() => {
        const rows = [...stream.pendingRows]; stream.pendingRows.clear();
        rows.forEach(row => { if (row.active) row.refresh(); });
      }, 150);
    };
    stream.notify = notify;
    stream.forbid = () => {
      state("forbidden"); clearTimeout(stream.fallbackTimer); clearTimeout(stream.updateTimer);
      clearTimeout(stream.reconnectTimer); stream.pendingRows.clear();
      stream.listeners.forEach(row => { row.pending = false; });
      stream.revisionWatcher?.stop(); stream.controller?.abort();
    };
    const fallback = () => {
      clearTimeout(stream.fallbackTimer);
      stream.fallbackTimer = setTimeout(() => {
        if (stream.stopped || stream.state === "forbidden") return;
        if (stream.state !== "connected") notify("legacy");
        fallback();
      }, FALLBACK_MS);
    };
    const connect = async () => {
      if (stream.stopped || stream.state === "forbidden") return;
      const controller = new AbortController(); stream.controller = controller;
      let inactivity;
      const touch = () => { clearTimeout(inactivity); inactivity = setTimeout(() => controller.abort(), 45000); };
      touch();
      try {
        const response = await fetch(`/api/cicd/events?spaceId=${encodeURIComponent(spaceId)}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" }, signal: controller.signal, cache: "no-store",
        });
        if (stream.stopped) return;
        if ([401, 403].includes(response.status)) { stream.forbid(); return; }
        if (!response.ok || !response.body?.getReader) throw new Error("CICD_EVENTS_UNAVAILABLE");
        const reader = response.body.getReader(), decoder = new TextDecoder();
        let buffer = "";
        while (!stream.stopped) {
          const chunk = await reader.read();
          if (chunk.done) break;
          touch(); buffer = (buffer + decoder.decode(chunk.value, { stream: true })).replace(/\r\n/g, "\n");
          if (buffer.length > 65536) throw new Error("CICD_EVENT_TOO_LARGE");
          let separator;
          while ((separator = buffer.indexOf("\n\n")) !== -1) {
            const block = buffer.slice(0, separator); buffer = buffer.slice(separator + 2);
            const event = block.split("\n").find(line => line.startsWith("event:"))?.slice(6).trim();
            if (!["connected", "cicd-update", "authorization"].includes(event)) continue;
            let value;
            try { value = JSON.parse(block.split("\n").filter(line => line.startsWith("data:")).map(line => line.slice(5).trimStart()).join("\n")); } catch { continue; }
            if (value.spaceId !== spaceId) continue;
            if (event === "authorization") { stream.forbid(); return; }
            if (event === "connected") {
              // On initial connect (no prior revision), skip notify — the mount
              // read is already in flight or just completed. Reconnections (prior
              // revision known) notify legacy readers; local readers compare the cursor.
              const reconnect = stream.revision !== null;
              const changed = reconnect && stream.revision !== value.revision;
              stream.revision = value.revision; stream.revisionWatcher?.observe(value.revision);
              stream.backoff = 1000; state("connected");
              if (reconnect) notify(changed ? "all" : "legacy");
              // Local status readers reconcile after subscription, closing the gap
              // between the mount snapshot and the stream's initial revision.
              notify("handshake");
              stream.revisionWatcher?.check();
            } else if (value.revision !== stream.revision) {
              // Revision is an opaque invalidation cursor. A snapshot handles gaps and reordering.
              stream.revision = value.revision; stream.revisionWatcher?.observe(value.revision); notify();
            }
          }
        }
        if (!stream.stopped) throw new Error("CICD_EVENTS_CLOSED");
      } catch {
        if (!stream.stopped && stream.state !== "forbidden") {
          state("reconnecting");
          // Spread pilot reconnections after a shared outage. Legacy Spaces
          // retain their cadence; no event or local revision interval changes.
          const pilot = [...stream.listeners].some(row => row.localRevision);
          const retryDelay = pilot ? Math.round(stream.backoff * (0.5 + Math.random() * 0.5)) : stream.backoff;
          stream.reconnectTimer = setTimeout(connect, retryDelay);
          stream.backoff = Math.min(stream.backoff * 2, 30000);
        }
      } finally { clearTimeout(inactivity); }
    };
    fallback(); Promise.resolve().then(connect);
  }
  const row = { listener, connection, localRevision, reconcileOnConnect, active: true, running: false, pending: false };
  const canRefresh = () => {
    if (!row.active || stream.stopped || stream.state === "forbidden") return false;
    try { return getToken() === token; } catch { return false; }
  };
  row.refresh = async () => {
    if (!canRefresh()) return;
    if (row.running) { row.pending = true; return; }
    row.running = true;
    do {
      row.pending = false;
      try { if (canRefresh()) await row.listener({ spaceId, revision: stream.revision }); } catch { /* The consumer displays its read error. */ }
    } while (canRefresh() && row.pending);
    row.running = false;
  };
  stream.listeners.add(row); connection(stream.state);
  if (reconcileOnConnect && stream.state === "connected") Promise.resolve().then(() => row.refresh());
  if (localRevision && !stream.revisionWatcher && stream.state !== "forbidden") {
    stream.revisionWatcher = createLocalRevisionWatcher({ spaceId, token, initialRevision: stream.revision,
      currentToken: () => { try { return getToken(); } catch { return null; } },
      onChange: revision => { stream.revision = revision; stream.notify("local"); }, onForbidden: stream.forbid });
    stream.revisionWatcher.check();
  }
  return () => {
    row.active = false;
    stream.listeners.delete(row); stream.pendingRows.delete(row);
    if (![...stream.listeners].some(item => item.localRevision)) { stream.revisionWatcher?.stop(); stream.revisionWatcher = null; }
    if (stream.listeners.size) return;
    stream.stopped = true; stream.controller?.abort();
    clearTimeout(stream.reconnectTimer); clearTimeout(stream.updateTimer); clearTimeout(stream.fallbackTimer);
    streams.delete(key);
  };
}

export default function useCicdEvents(spaceId, onUpdate, enabled = true, { localRevision = false, reconcileOnConnect = false } = {}) {
  const callback = useRef(onUpdate); callback.current = onUpdate;
  let authToken;
  try { authToken = getToken(); } catch { authToken = null; }
  const connectionScope = JSON.stringify([spaceId, authToken, enabled, localRevision, reconcileOnConnect]);
  const [connection, setConnection] = useState(null);
  useEffect(() => {
    if (!enabled || !spaceId) return;
    let active = true, running = false, pending = false;
    let latestSignal;
    const refresh = async signal => {
      latestSignal = signal;
      if (running) { pending = true; return; }
      running = true;
      do {
        pending = false;
        try { await callback.current?.(latestSignal); } catch { /* Consumers retain their read error. */ }
      } while (active && pending);
      running = false;
    };
    const close = subscribeCicdEvents(spaceId, refresh, state => { if (active) setConnection({ scope: connectionScope, state }); }, { localRevision, reconcileOnConnect });
    return () => { active = false; close(); };
  }, [spaceId, enabled, authToken, localRevision, reconcileOnConnect, connectionScope]);
  return connection?.scope === connectionScope ? connection.state : "connecting";
}
